import { Check, X, Zap, Crown, Monitor, Building2, Rocket, Shield } from "lucide-react";
import { Link } from "react-router-dom";

const FREE_FEATURES = [
  "Up to 20 Products",
  "Amazon Link Import",
  "ASIN Extraction",
  "Auto-Sync (WP-Cron)",
  "Product Cards + Grid",
  "Shortcodes",
  "Gutenberg Block",
  "Price History Sparklines",
  "Rotating User-Agents",
  "Transient Caching",
  "Error Logging",
  "Amazon Disclaimer",
];

const FREE_LOCKED = [
  "Unlimited Products",
  "Price Drop Email Alerts",
  "Elementor Widget",
  "WP-CLI Commands",
  "CSV Bulk Import",
  "Click Analytics",
  "Link Cloaking",
  "Comparison Tables",
  "SEO Schema",
];

const PRO_FEATURES = [
  "Unlimited Products",
  "Everything in Free +",
  "Price Drop Email Alerts",
  "Elementor Widget",
  "WP-CLI Sync & Import",
  "CSV Bulk Import",
  "Multi-Image Extraction",
  "Feature Bullet Points",
  "Discount % Detection",
  "Reviews Count",
  "Smart Sync (Click Priority)",
  "Link Cloaking (/go/...)",
  "Click Analytics + CTR",
  "Comparison Tables",
  "SEO Schema (JSON-LD)",
  "Sticky Buy CTA",
  "Exit Intent Popup",
  "Countdown Timer",
  "Dark Mode",
  "List + Carousel Layouts",
  "AdSense Integration",
  "Auto-Hide Out-of-Stock",
  "Lowest Price Badge",
];

const PRO_PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 29,
    sites: "1 Website",
    icon: Monitor,
    color: "#52525B",
    popular: false,
  },
  {
    id: "business",
    name: "Business",
    price: 58,
    sites: "3 Websites",
    icon: Building2,
    color: "#FF7300",
    popular: true,
  },
  {
    id: "agency",
    name: "Agency",
    price: 118,
    sites: "7 Websites",
    icon: Rocket,
    color: "#2563EB",
    popular: false,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 299,
    sites: "Unlimited",
    icon: Crown,
    color: "#09090B",
    popular: false,
  },
];

export default function PricingSection({ onDownload, onBuyPro }) {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="inline-flex items-center gap-2 bg-[#FF7300]/10 text-[#FF7300] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Choose Your Edition
          </span>
          <h2 className="font-display font-extrabold text-[#09090B] text-3xl sm:text-4xl tracking-tight mb-4">
            Free vs PRO
          </h2>
          <p className="text-[#52525B] text-base md:text-lg max-w-xl mx-auto">
            Start free with essentials or unlock the full power of affiliate automation.
          </p>
        </div>

        {/* Free Plan Card */}
        <div className="border border-[#E4E4E7] rounded-xl p-7 bg-white mb-10 max-w-2xl mx-auto" data-testid="pricing-free-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#09090B] flex items-center justify-center">
                <Zap size={18} className="text-white" />
              </div>
              <div>
                <h3 className="font-display font-bold text-[#09090B] text-lg">Free</h3>
                <p className="text-xs text-[#A1A1AA]">20 files &middot; v1.0.0</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-2xl font-bold text-[#09090B]">$0</span>
              <button
                data-testid="pricing-free-download-btn"
                onClick={() => onDownload("free")}
                className="flex items-center justify-center gap-2 border border-[#09090B] text-[#09090B] bg-white hover:bg-[#F9FAFB] rounded-md px-6 py-2.5 text-sm font-semibold transition-colors cursor-pointer"
              >
                Download Free
              </button>
            </div>
          </div>
          <p className="text-sm text-[#52525B] mt-4 mb-4">
            Everything you need to start importing Amazon products and displaying them on WordPress. Up to 20 products.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1.5">
            {FREE_FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-[#09090B]">
                <Check size={14} className="text-[#067d06] flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
          {/* Locked PRO features */}
          <div className="border-t border-[#E4E4E7] mt-4 pt-3">
            <p className="text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">Upgrade to unlock</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              {FREE_LOCKED.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                  <X size={14} className="text-[#D4D4D8] flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PRO Plans Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#FF7300] text-white px-4 py-1.5 rounded-full text-sm font-bold">
            <Crown size={16} /> PRO Plans
          </div>
          <p className="text-sm text-[#52525B] mt-2">All Free features + {PRO_FEATURES.length} PRO-exclusive features &middot; Unlimited products</p>
        </div>

        {/* PRO Plans Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10" data-testid="pro-plans-grid">
          {PRO_PLANS.map((plan) => {
            const Icon = plan.icon;
            return (
              <div
                key={plan.id}
                data-testid={`pricing-plan-${plan.id}`}
                className={`relative border rounded-xl p-6 bg-white transition-all hover:shadow-lg hover:-translate-y-1 ${
                  plan.popular
                    ? "border-2 border-[#FF7300] shadow-[0_0_20px_rgba(255,115,0,0.1)]"
                    : "border-[#E4E4E7]"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF7300] text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    MOST POPULAR
                  </div>
                )}
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: plan.color + "15" }}
                >
                  <Icon size={20} style={{ color: plan.color }} />
                </div>
                <h4 className="font-display font-bold text-[#09090B] text-base">{plan.name}</h4>
                <p className="text-xs text-[#A1A1AA] mb-3">{plan.sites}</p>
                <div className="mb-4">
                  <span className="text-3xl font-extrabold text-[#09090B]">${plan.price}</span>
                  <span className="text-sm text-[#A1A1AA]">/year</span>
                </div>
                <button
                  data-testid={`pricing-buy-${plan.id}-btn`}
                  onClick={() => onBuyPro(plan.id)}
                  className={`w-full flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-bold transition-all cursor-pointer border-0 ${
                    plan.popular
                      ? "bg-[#FF7300] text-white hover:bg-[#E66800]"
                      : "bg-[#09090B] text-white hover:bg-[#27272A]"
                  }`}
                >
                  <Crown size={14} />
                  Get {plan.name}
                </button>
                <p className="text-[10px] text-center text-[#A1A1AA] mt-2">33 files &middot; v2.0.0</p>
              </div>
            );
          })}
        </div>

        {/* PRO Features List */}
        <div className="border border-[#FF7300]/20 rounded-xl p-7 bg-[#FF7300]/[0.02]" data-testid="pro-features-list">
          <div className="flex items-center gap-2 mb-4">
            <Crown size={16} className="text-[#FF7300]" />
            <span className="text-sm font-bold text-[#FF7300]">All PRO Plans Include</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {PRO_FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-[#09090B] font-medium">
                <Check size={14} className="text-[#FF7300] flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* 30-Day Money-Back Guarantee */}
        <div className="mt-8 text-center" data-testid="guarantee-section">
          <div className="inline-flex items-center gap-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-full px-6 py-3">
            <Shield size={20} className="text-[#FF7300]" />
            <span className="text-sm font-bold text-[#9a3412]">30-Day Money-Back Guarantee</span>
            <span className="text-xs text-[#52525B]">&middot;</span>
            <Link to="/refund-policy" className="text-xs font-semibold text-[#FF7300] hover:underline" data-testid="refund-policy-link">
              Read Policy
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
